import { Canvas } from '@react-three/fiber';
import { ContactShadows, Environment, Scroll, ScrollControls } from '@react-three/drei';
import { MeatModel } from './MeatModel.jsx';
import { SCROLL_PAGES } from './glbCatalog.js';

function Scene() {
  return (
    <>
      <color attach="background" args={['#f6eaea']} />
      <ambientLight intensity={0.42} />
      <spotLight
        position={[4, 6, 4]}
        angle={0.35}
        penumbra={0.85}
        intensity={1.65}
        castShadow
        shadow-mapSize={[2048, 2048]}
      />
      <spotLight position={[-5, 3, -2]} intensity={0.62} color="#fecaca" />
      <directionalLight position={[-3, 8, 6]} intensity={0.65} color="#fff7f7" />

      <MeatModel />

      <ContactShadows
        position={[0, -1.35, 0]}
        opacity={0.48}
        scale={12}
        blur={2.1}
        far={4}
        color="#8c8b8b"
      />

      <Environment preset="city" />
    </>
  );
}

function SectionCard({ eyebrow, title, body, align = 'left' }) {
  const alignClass =
    align === 'right' ? 'ml-auto text-right items-end' : 'mr-auto text-left items-start';
  return (
    <div
      className={`flex max-w-2xl flex-col gap-4 rounded-xl border border-red-100 bg-white/92 px-10 py-9 shadow-xl shadow-red-950/12 backdrop-blur-md ${alignClass}`}
    >
      <p className="text-sm font-semibold uppercase tracking-wide text-red-600">{eyebrow}</p>
      <h2 className="text-4xl font-bold leading-tight tracking-tight text-gray-900 md:text-5xl">{title}</h2>
      <p className="text-xl leading-relaxed text-gray-700">{body}</p>
    </div>
  );
}

function ScrollSections() {
  return (
    <div className="w-full">
      <section className="relative flex min-h-screen flex-col justify-center px-6 pt-24 md:px-16">
        <div className="max-w-4xl rounded-2xl border border-red-100 bg-white/90 px-8 py-10 shadow-2xl shadow-red-950/10 backdrop-blur-sm md:px-12 md:py-12">
          <p className="mb-5 text-base font-semibold uppercase tracking-wide text-red-600">MeatShop Platform</p>
          <h1 className="text-6xl font-bold leading-[1.02] tracking-tight text-gray-900 md:text-8xl">
            Premium cuts,
            <span className="block text-red-600"> premium operations.</span>
          </h1>
          <p className="mt-8 max-w-3xl text-2xl leading-relaxed text-gray-700">
            MeatShop helps butcher owners grow online with one platform to showcase products, run a
            built-in POS, and manage inventory, expenses, and earnings with daily, monthly, or
            custom date range reporting. Customers can discover nearby meat shops, order online,
            choose pickup later, or contact the shop for direct delivery.
          </p>
          <div className="mt-10 flex flex-wrap gap-5">
            <a
              href="#features"
              className="pointer-events-auto inline-flex items-center justify-center rounded-lg bg-red-600 px-9 py-4 text-base font-semibold text-white shadow-lg transition hover:bg-red-700"
            >
              Explore platform features
            </a>
          </div>
          <div className="mt-8 grid max-w-3xl grid-cols-1 gap-3 text-lg text-gray-700 md:grid-cols-3">
            <p className="rounded-lg border border-red-100 bg-red-50/70 px-4 py-3 font-medium">Inventory accuracy</p>
            <p className="rounded-lg border border-red-100 bg-red-50/70 px-4 py-3 font-medium">Faster checkout flow</p>
            <p className="rounded-lg border border-red-100 bg-red-50/70 px-4 py-3 font-medium">Multi-tenant storefronts</p>
          </div>
        </div>
      </section>

      <section
        id="features"
        className="relative flex min-h-screen items-center justify-end px-6 md:px-16"
      >
        <SectionCard
          eyebrow="Owner portal"
          title="Dashboard that answers the day in seconds"
          body="Sales overview, revenue trends, and receivables at a glance — so you see what matters before the rush starts."
          align="right"
        />
      </section>

      <section className="relative flex min-h-screen items-center px-6 md:px-16">
        <SectionCard
          eyebrow="Operations"
          title="Sales & inventory without spreadsheet chaos"
          body="Record cash or credit sales, move stock with confidence, and keep prices aligned with how you actually sell by the kilo or pack."
          align="left"
        />
      </section>

      <section className="relative flex min-h-screen items-center justify-end px-6 md:px-16">
        <SectionCard
          eyebrow="Relationships"
          title="Customers & utang, under control"
          body="Know who owes what, track partial payments, and keep your regulars onside without losing the paper trail."
          align="right"
        />
      </section>

      <section className="relative flex min-h-screen items-center px-6 md:px-16">
        <SectionCard
          eyebrow="Finance"
          title="Expenses & receivables in one ledger mindset"
          body="Categorize spend, watch margins, and close the loop between what went out and what is still coming in."
          align="left"
        />
      </section>

      <section className="relative flex min-h-screen items-center justify-end px-6 md:px-16">
        <SectionCard
          eyebrow="Storefront"
          title="A branded shop for every tenant"
          body="Customers browse your cuts, fill a cart, and place orders on their subdomain — isolated data, unified platform."
          align="right"
        />
      </section>

      <section className="relative flex min-h-screen flex-col items-center justify-center px-6 pb-24 pt-16 md:px-16">
        <div className="max-w-3xl rounded-2xl border border-red-100 bg-white/92 px-8 py-10 text-center shadow-2xl shadow-red-950/10 md:px-12">
          <h2 className="text-5xl font-bold tracking-tight text-gray-900 md:text-6xl">Ready when you are</h2>
          <p className="mt-6 text-2xl text-gray-700">
            Deploy the admin dashboard, spin up storefronts, and keep the back office in sync with
            how real meat businesses run.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <span className="rounded-lg border border-red-200 bg-red-50 px-9 py-4 text-base font-semibold text-red-800">
              Connect your deploy URL here
            </span>
          </div>
        </div>
      </section>

      <footer className="relative px-6 pb-12 md:px-16">
        <div className="mx-auto max-w-3xl rounded-xl border border-red-100 bg-white/90 px-6 py-5 text-center shadow-lg shadow-red-950/10">
          <p className="text-base text-gray-700">
            Developed by{' '}
            <a
              href="https://pietone.com"
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-red-700 underline decoration-red-300 underline-offset-4 transition hover:text-red-800"
            >
              PieTone
            </a>
            . Curious about our team and services? Visit{' '}
            <a
              href="https://pietone.com"
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-red-700 underline decoration-red-300 underline-offset-4 transition hover:text-red-800"
            >
              pietone.com
            </a>
            .
          </p>
        </div>
      </footer>
    </div>
  );
}

function Nav() {
  return (
    <header className="pointer-events-none fixed left-0 right-0 top-0 z-20 flex items-center justify-between px-6 py-6 md:px-12">
      <a href="/" className="pointer-events-auto text-2xl font-bold tracking-tight text-gray-900">
        Meat<span className="text-red-600">Shop</span>
      </a>
      <nav className="pointer-events-auto hidden items-center gap-5 text-base md:flex">
        <a
          href="https://gmeat.pietone.com"
          className="rounded-lg border border-red-200 bg-red-50 px-5 py-2.5 font-semibold text-red-800 transition hover:bg-red-100"
          target="_blank"
          rel="noreferrer"
        >
          Go to customer page
        </a>
        <a
          href="https://meatshop.pietone.com"
          className="rounded-lg bg-red-600 px-5 py-2.5 font-semibold text-white shadow-md transition hover:bg-red-700"
          target="_blank"
          rel="noreferrer"
        >
          Go to shop owner page
        </a>
      </nav>
    </header>
  );
}

export default function App() {
  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-[#fff8f8] via-[#f7dede]/45 to-[#fff8f8]">
      <div className="pointer-events-none fixed inset-0 z-[1] bg-[radial-gradient(ellipse_at_center,_rgba(136,8,8,0.16)_0%,_rgba(255,255,255,0)_58%)]" />
      <div className="pointer-events-none fixed inset-x-0 top-0 z-[15] h-44 bg-gradient-to-b from-white via-white/85 to-transparent" />
      <Nav />

      <div className="fixed inset-0 z-0">
        <Canvas
          shadows
          camera={{ position: [0, 0.2, 5.2], fov: 38 }}
          dpr={[1, 2]}
        >
          <ScrollControls pages={SCROLL_PAGES} damping={0.22}>
            <Scene />
            <Scroll html style={{ width: '100%' }}>
              <ScrollSections />
            </Scroll>
          </ScrollControls>
        </Canvas>
      </div>
    </div>
  );
}
